import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// "Lebende Zeichnung" wie auf kalakutok.com: fixierter Layer hinter dem Inhalt. An wechselnden
// freien Stellen zeichnen sich feine technische Konstruktionen (Masslinien, Kreise mit Radius,
// Koordinatenkreuze, bemasste Rahmen, Winkel), stehen kurz und blenden aus. Nur Graphit.
//
// Leichtgewichtig: ein SVG, 2-3 Figuren gleichzeitig, reine CSS-Keyframes (index.css, .bp-*).
// Eine neue Figur entsteht erst, wenn die alte ihre Lebensdauer-Animation beendet hat
// (animationend), es gibt keinen JS-Frame-Loop. Bei prefers-reduced-motion gar nicht: der
// globale Killswitch setzt Animationsdauern auf ~0, die animationend-Kette liefe sonst leer.
//
// Per Portal als erstes Kind von <body> mit z-index 0: so liegt die Ebene unter dem statischen
// Hero/Kontakt (z-index 1 bzw. spaeter im DOM) UND unter #root (z-index 2).

const ARTEN = ['masslinie', 'kreis', 'koordinate', 'rahmen', 'winkel'];
const LEBENSDAUER_S = 8;

const zufall = (min, max) => min + Math.random() * (max - min);
const raster = (wert, schritt = 5) => Math.round(wert / schritt) * schritt;

// Ungefaehre Ausdehnung je Figur, fuer die Platzsuche ohne Ueberlappung
function ausdehnung(art, groesse, variante) {
  switch (art) {
    case 'masslinie':
      return variante === 0 ? { w: groesse, h: 40 } : { w: 60, h: groesse };
    case 'kreis':
      return { w: groesse * 2 + 70, h: groesse * 2 + 20 };
    case 'koordinate':
      return { w: 170, h: 110 };
    case 'rahmen':
      return { w: groesse, h: groesse * 0.62 + 34 };
    default:
      return { w: groesse + 40, h: groesse * 0.8 + 20 };
  }
}

// Alles, worueber keine Linie laufen soll: Text, Bedienelemente, Karten, Grafiken
const INHALT_SELEKTOR =
  'h1,h2,h3,h4,p,li,a,button,label,input,textarea,table,img,video,svg:not(.blueprint-backdrop),form,dl,[role="button"]';

// Prueft ein Punktraster der geplanten Flaeche per elementFromPoint (der Layer selbst hat
// pointer-events: none und wird uebersprungen)
function ueberdecktInhalt(x, y, w, h) {
  for (let c = 0; c <= 5; c++) {
    for (let r = 0; r <= 4; r++) {
      const el = document.elementFromPoint(x + (w * c) / 5, y + (h * r) / 4);
      if (el?.closest(INHALT_SELEKTOR)) return true;
    }
  }
  return false;
}

function erzeugeFigur(id, vw, vh, andere, verzoegerung) {
  const art = ARTEN[Math.floor(Math.random() * ARTEN.length)];
  const groesse =
    art === 'kreis' ? zufall(22, 46)
      : art === 'koordinate' ? 0
        : art === 'winkel' ? zufall(110, 180)
          : zufall(130, Math.min(260, vw * 0.45));
  const variante = Math.random() < 0.5 ? 0 : 1;
  const { w, h } = ausdehnung(art, groesse, variante);
  const rand = 24;

  const basis = { id, w, h, art, groesse, variante, r1: Math.random(), r2: Math.random(), verzoegerung };
  for (let versuch = 0; versuch < 24; versuch++) {
    const x = zufall(rand, Math.max(rand, vw - w - rand));
    const y = zufall(rand + 64, Math.max(rand + 64, vh - h - rand));
    const kollision = andere.some(
      (o) => !o.leer && x < o.x + o.w + 40 && x + w + 40 > o.x && y < o.y + o.h + 40 && y + h + 40 > o.y
    );
    if (!kollision && !ueberdecktInhalt(x, y, w, h)) return { ...basis, x, y };
  }
  // Kein freier Platz (textlastige Stelle): diesen Zyklus als unsichtbarer Timer aussetzen
  return { ...basis, x: 0, y: 0, leer: true };
}

function BlueprintBackdrop() {
  const [ziel, setZiel] = useState(null);
  const [ansicht, setAnsicht] = useState(null);
  const [figuren, setFiguren] = useState([]);
  const naechsteId = useRef(0);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const ebene = document.createElement('div');
    ebene.className = 'blueprintEbene';
    ebene.setAttribute('aria-hidden', 'true');
    document.body.prepend(ebene);
    setZiel(ebene);

    const messen = () => setAnsicht({ w: window.innerWidth, h: window.innerHeight });
    messen();

    const w = window.innerWidth;
    const h = window.innerHeight;
    const anzahl = w < 768 ? 2 : 3;
    const start = [];
    for (let i = 0; i < anzahl; i++) {
      // gestaffelter Start, damit nie alle Figuren gleichzeitig kommen und gehen
      start.push(erzeugeFigur(naechsteId.current++, w, h, start, 1.2 + i * (LEBENSDAUER_S / anzahl)));
    }
    setFiguren(start);

    // Nach dem Scrollen: Figuren, unter die inzwischen Inhalt geschoben wurde, zuegig ausblenden
    let zeitgeber;
    const beimScrollen = () => {
      window.clearTimeout(zeitgeber);
      zeitgeber = window.setTimeout(() => {
        ebene.querySelectorAll('.bp-slot').forEach((slot) => {
          const { x, y, w: sw, h: sh } = slot.dataset;
          if (ueberdecktInhalt(Number(x), Number(y), Number(sw), Number(sh))) slot.style.opacity = '0';
        });
      }, 120);
    };

    window.addEventListener('resize', messen);
    window.addEventListener('scroll', beimScrollen, { passive: true });
    return () => {
      window.clearTimeout(zeitgeber);
      window.removeEventListener('resize', messen);
      window.removeEventListener('scroll', beimScrollen);
      ebene.remove();
    };
  }, []);

  const ersetze = useCallback(
    (id) => {
      if (!ansicht) return;
      setFiguren((aktuell) => {
        const andere = aktuell.filter((f) => f.id !== id);
        // In einem Hintergrund-Tab keine neuen Figuren: kurze Pause, dann weiter
        const verzoegerung = document.hidden ? 2 : zufall(0.3, 1.4);
        const neu = erzeugeFigur(naechsteId.current++, ansicht.w, ansicht.h, andere, verzoegerung);
        return aktuell.map((f) => (f.id === id ? neu : f));
      });
    },
    [ansicht]
  );

  if (!ziel || !ansicht) return null;

  return createPortal(
    <svg
      width={ansicht.w}
      height={ansicht.h}
      viewBox={`0 0 ${ansicht.w} ${ansicht.h}`}
      className="blueprint-backdrop"
    >
      {figuren.map((figur) => (
        <g key={figur.id} className="bp-slot" data-x={figur.x} data-y={figur.y} data-w={figur.w} data-h={figur.h}>
          <g
            className="bp-figure"
            style={{ animationDelay: `${figur.verzoegerung}s`, animationDuration: `${LEBENSDAUER_S}s` }}
            transform={`translate(${figur.x} ${figur.y})`}
            onAnimationEnd={(ereignis) => {
              if (ereignis.target === ereignis.currentTarget) ersetze(figur.id);
            }}
          >
            {!figur.leer && <Figur figur={figur} />}
          </g>
        </g>
      ))}
    </svg>,
    ziel
  );
}

// Ein Strich, der sich zeichnet; "ab" = Start relativ zum Figurstart (s)
function Strich({ d, ab, basis, gestrichelt }) {
  return (
    <path
      d={d}
      // gestrichelte Linien blenden nur ein (dasharray ist schon belegt)
      pathLength={gestrichelt ? undefined : 1}
      className={gestrichelt ? 'bp-stroke bp-dashed' : 'bp-stroke'}
      style={{ animationDelay: `${basis + ab}s` }}
    />
  );
}

function Beschriftung({ x, y, children, ab, basis, anker = 'middle' }) {
  return (
    <text x={x} y={y} textAnchor={anker} className="bp-label" style={{ animationDelay: `${basis + ab}s` }}>
      {children}
    </text>
  );
}

function Figur({ figur }) {
  const { art, groesse, variante, r1, r2, verzoegerung: basis } = figur;

  if (art === 'masslinie') {
    const L = groesse;
    const wert = raster(L * 0.8).toString();
    if (variante === 0) {
      return (
        <>
          <Strich d={`M0 4 V32 M${L} 4 V32`} ab={0} basis={basis} />
          <Strich d={`M0 22 H${L}`} ab={0.5} basis={basis} />
          <Strich d={`M8 18 L0 22 L8 26 M${L - 8} 18 L${L} 22 L${L - 8} 26`} ab={1.4} basis={basis} />
          <Beschriftung x={L / 2} y={15} ab={1.8} basis={basis}>{wert}</Beschriftung>
        </>
      );
    }
    return (
      <>
        <Strich d={`M4 0 H44 M4 ${L} H44`} ab={0} basis={basis} />
        <Strich d={`M34 0 V${L}`} ab={0.5} basis={basis} />
        <Strich d={`M30 8 L34 0 L38 8 M30 ${L - 8} L34 ${L} L38 ${L - 8}`} ab={1.4} basis={basis} />
        <Beschriftung x={46} y={L / 2 + 3} anker="start" ab={1.8} basis={basis}>{wert}</Beschriftung>
      </>
    );
  }

  if (art === 'kreis') {
    const r = groesse;
    const cx = r + 10;
    const cy = r + 10;
    const text = variante === 0 ? `R ${raster(r, 2)}` : `Ø ${raster(r * 2, 2)}`;
    return (
      <>
        <Strich d={`M${cx + r} ${cy} A${r} ${r} 0 1 1 ${cx - r} ${cy} A${r} ${r} 0 1 1 ${cx + r} ${cy}`} ab={0} basis={basis} />
        <Strich d={`M${cx - 8} ${cy} H${cx + 8} M${cx} ${cy - 8} V${cy + 8}`} ab={0.9} basis={basis} />
        <Strich d={`M${cx} ${cy} L${cx + r * 0.7} ${cy - r * 0.7} L${cx + r + 34} ${cy - r * 0.7}`} ab={1.3} basis={basis} />
        <Beschriftung x={cx + r * 0.7 + 6} y={cy - r * 0.7 - 5} anker="start" ab={2} basis={basis}>{text}</Beschriftung>
      </>
    );
  }

  if (art === 'koordinate') {
    const x = Math.round(10 + r1 * 80);
    const y = Math.round(10 + r2 * 80);
    return (
      <>
        <Strich d="M0 60 H150" gestrichelt ab={0} basis={basis} />
        <Strich d="M60 0 V110" gestrichelt ab={0.3} basis={basis} />
        <Strich d="M52 60 H68 M60 52 V68" ab={1} basis={basis} />
        <Strich d="M60 60 m-5 0 a5 5 0 1 0 10 0 a5 5 0 1 0 -10 0" ab={1.2} basis={basis} />
        <Beschriftung x={70} y={52} anker="start" ab={1.8} basis={basis}>{`(${x}, ${y})`}</Beschriftung>
      </>
    );
  }

  if (art === 'rahmen') {
    const W = groesse;
    const H = raster(groesse * 0.62);
    return (
      <>
        <Strich d={`M0 34 H${W} V${34 + H} H0 Z`} gestrichelt ab={0} basis={basis} />
        <Strich
          d={`M0 46 V34 H12 M${W - 12} 34 H${W} V46 M${W} ${22 + H} V${34 + H} H${W - 12} M12 ${34 + H} H0 V${22 + H}`}
          ab={1}
          basis={basis}
        />
        <Strich d={`M0 12 V26 M${W} 12 V26 M0 19 H${W}`} ab={1.3} basis={basis} />
        <Beschriftung x={W / 2} y={13} ab={2} basis={basis}>{`${raster(W)} × ${H}`}</Beschriftung>
      </>
    );
  }

  // Winkel
  const L = groesse;
  const grad = [30, 45, 60][Math.floor(r1 * 3)];
  const rad = (grad * Math.PI) / 180;
  const oy = L * 0.8;
  const ex = Math.cos(rad) * L;
  const ey = oy - Math.sin(rad) * L;
  const ar = 38;
  return (
    <>
      <Strich d={`M0 ${oy} H${L}`} ab={0} basis={basis} />
      <Strich d={`M0 ${oy} L${ex} ${ey}`} ab={0.5} basis={basis} />
      <Strich d={`M${ar} ${oy} A${ar} ${ar} 0 0 0 ${Math.cos(rad) * ar} ${oy - Math.sin(rad) * ar}`} ab={1.3} basis={basis} />
      <Beschriftung x={ar + 14} y={oy - 10} anker="start" ab={1.9} basis={basis}>{`${grad}°`}</Beschriftung>
    </>
  );
}

export default BlueprintBackdrop;
